// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useRef, useState } from 'react'
import { Database, HardDriveDownload, Loader2, Pause, Play, ShieldCheck, AlertCircle } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'

/**
 * D-SHIP-19: POD document storage card (SaaS-admin, pharmacy detail page).
 *
 * Shows the two Phase gates (storage provisioned, migration applied) and the live
 * backfill progress (DB base64 → the pharmacy's private blob storage). The Run
 * control drives the server's bounded batch endpoint call-by-call from THIS
 * browser session — pausing simply stops issuing the next call; the server never
 * runs unattended. Copy-only: the DB's inline bytes are untouched by the run.
 */
export default function PharmacyPodStorageCard({ pharmacyId }: { pharmacyId: string }) {
  const [status, setStatus] = useState<api.PodStorageStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [provisioning, setProvisioning] = useState(false)
  const [running, setRunning] = useState(false)
  const [lastBatch, setLastBatch] = useState<api.PodBackfillBatchResult | null>(null)
  const [failedTotal, setFailedTotal] = useState(0)
  const [busy, setBusy] = useState(false)   // transient DB congestion — auto-retrying
  const runningRef = useRef(false)

  async function refresh() {
    try { setStatus(await api.getPodStorageStatus(pharmacyId)); setError(null) }
    catch (e) { setError(friendlyError(e)) }
  }
  useEffect(() => { refresh() }, [pharmacyId])
  useEffect(() => () => { runningRef.current = false }, [])   // unmount = stop issuing calls

  async function provision() {
    setProvisioning(true); setError(null)
    try { await api.ensurePharmacyStorage(pharmacyId); await refresh() }
    catch (e) { setError(friendlyError(e)) }
    finally { setProvisioning(false) }
  }

  // Watermark cursor: failing rows are skipped for the rest of THIS run instead of
  // livelocking the head of the queue; a fresh Start (cursor reset) retries them.
  const cursorRef = useRef<{ created: string; id: string } | undefined>(undefined)

  async function runLoop(fresh: boolean) {
    if (runningRef.current) return
    if (fresh) cursorRef.current = undefined
    runningRef.current = true
    setRunning(true); setError(null)
    let busyStreak = 0
    try {
      while (runningRef.current) {
        let r: api.PodBackfillBatchResult
        try {
          r = await api.runPodBackfillBatch(pharmacyId, 50, cursorRef.current)
          busyStreak = 0
          setBusy(false)
        } catch (e: any) {
          // Transient DB congestion (server says db_busy, or any 5xx while the tiny DB is
          // slammed): wait and continue rather than killing a multi-hour supervised run.
          // Ten misses in a row = something real; stop and show it.
          busyStreak++
          if (busyStreak >= 10) throw e
          setBusy(true)
          await new Promise(res => setTimeout(res, 8000))
          continue
        }
        setLastBatch(r)
        setFailedTotal(t => t + r.failed)
        if (r.next_after_created && r.next_after_id)
          cursorRef.current = { created: r.next_after_created, id: r.next_after_id }
        await refresh()
        // scanned 0 = this pass reached the end (remaining may still count skipped rows).
        // remaining < 0 = the server's count timed out (busy DB) — bookkeeping only, keep going.
        if (r.scanned === 0 || r.remaining === 0) break
        // Breathe between batches — the tenant DB is small; gentle by design.
        await new Promise(res => setTimeout(res, 1500))
      }
    } catch (e) { setError(friendlyError(e)) }
    finally { runningRef.current = false; setRunning(false); setBusy(false); refresh() }
  }
  function pause() { runningRef.current = false; setRunning(false) }

  const s = status
  const pct = s ? s.percent : 0

  return (
    <div style={card}>
      <div style={cardHead}>
        <Database size={15} />
        <span>POD document storage</span>
        {s?.ready && s.remaining === 0 && s.migrated > 0 && (
          <span style={okBadge}><ShieldCheck size={12} /> fully migrated</span>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: '#555', marginBottom: 10 }}>
        Delivery photos / POD PDFs moving from the database to this pharmacy's private blob
        storage. Copy-only — the database keeps its bytes until the separately-approved cleanup.
      </div>

      {error && <div style={errBox}><AlertCircle size={13} /> {error}</div>}

      {!s ? (
        <div style={{ fontSize: 13, color: '#777' }}><Loader2 size={13} className="spin" /> Loading…</div>
      ) : (
        <>
          <div style={gateRow}>
            <Gate ok={s.storage_configured} label="Storage account provisioned" />
            <Gate ok={s.migration_applied} label="Tenant migration 336 applied" />
          </div>

          {!s.storage_configured && (
            <button style={btn} disabled={provisioning} onClick={provision}>
              {provisioning ? <Loader2 size={13} className="spin" /> : <HardDriveDownload size={13} />}
              Provision storage account
            </button>
          )}

          {s.ready && (
            <>
              <div style={barOuter} aria-label={`backfill ${pct}% complete`}>
                <div style={{ ...barInner, width: `${pct}%` }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#444', marginTop: 4 }}>
                <span><b>{s.migrated.toLocaleString()}</b> migrated</span>
                <span><b>{s.remaining < 0 ? '—' : s.remaining.toLocaleString()}</b> remaining</span>
                <span><b>{pct}%</b></span>
              </div>
              {failedTotal > 0 && (
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                  {failedTotal} document(s) skipped this session (inline copy preserved; re-run picks them up)
                </div>
              )}
              {busy && running && (
                <div style={{ fontSize: 12, color: '#b45309', marginTop: 4 }}>
                  Database is busy — waiting and retrying automatically…
                </div>
              )}
              {lastBatch && running && !busy && (
                <div style={{ fontSize: 11.5, color: '#888', marginTop: 2 }}>
                  last batch: {lastBatch.migrated} in {(lastBatch.ms / 1000).toFixed(1)}s
                </div>
              )}
              <div style={{ marginTop: 10 }}>
                {s.remaining > 0 && (running ? (
                  <button style={btn} onClick={pause}><Pause size={13} /> Pause</button>
                ) : (
                  <button style={btn} onClick={() => runLoop(!cursorRef.current)}>
                    <Play size={13} /> {s.migrated > 0 ? 'Resume backfill' : 'Start backfill'}
                  </button>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}

function Gate({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: ok ? '#15803d' : '#9a3412' }}>
      <span style={{ width: 8, height: 8, borderRadius: 4, background: ok ? '#22c55e' : '#f97316' }} />
      {label}
    </span>
  )
}

const card: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', marginTop: 14, background: '#fff' }
const cardHead: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13.5, marginBottom: 6 }
const okBadge: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: '#15803d', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '2px 8px', marginLeft: 'auto' }
const gateRow: React.CSSProperties = { display: 'flex', gap: 18, margin: '6px 0 10px' }
const barOuter: React.CSSProperties = { height: 10, borderRadius: 6, background: '#f1f5f9', border: '1px solid #e2e8f0', overflow: 'hidden' }
const barInner: React.CSSProperties = { height: '100%', background: 'linear-gradient(90deg,#3b82f6,#2563eb)', transition: 'width .4s ease' }
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, font: '600 12.5px "Segoe UI",system-ui', padding: '7px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', cursor: 'pointer' }
const errBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }
